import { appOverride, appVariant, VariantMap } from '@util/AppUtils';
import { Environment } from '@util/Environment';
import {
  App,
  MenoPlan,
  MoodPlan,
  NervaPlan,
  PainPlan,
  ProductAddons,
  QuitPlan,
  SubscriptionPlan,
  TrialPeriodDays,
  TrialPlan,
} from 'hydra-data-types';
import { isNonEmptyString } from 'ts-utils';
import { couponCodeFromQueryParam } from '../config/AssessmentUtils';
import { PurchaseState } from '../store/purchase/PurchaseState';

/**
 * Deprecated plans that are no longer available for purchase will not be included in the stripe
 * price query.
 */
const deprecatedPlans = [NervaPlan.NervaFullSubscription3MonthAnnualPrice, NervaPlan.NervaProgram];

// filter out deprecated nerva plans from the list of available plans
const activeNervaPlans = Object.values(NervaPlan).filter((plan) => !deprecatedPlans.includes(plan));

type SubscriptionInterval = {
  inText: string;
  inWeeks: number;
  inMonths: number;
};

export const AVAILABLE_PLANS = appVariant<SubscriptionPlan[]>({
  nerva: Object.values(activeNervaPlans),
  meno: Object.values(MenoPlan),
  quit: Object.values(QuitPlan),
  pain: Object.values(PainPlan),
});

/** @deprecated This was the plan used to compare discounts to. */
export const BASELINE_PURCHASE_PLAN = appVariant<SubscriptionPlan>({
  nerva: NervaPlan.NervaFullSubscription,
  meno: MenoPlan.MenoFullSubscription,
  quit: QuitPlan.QuitFullSubscription3Month,
  pain: PainPlan.SubFullAnnual_199,
});

export const AVAILABLE_ADDON_PLANS = appVariant<ProductAddons[]>({
  // When addon plans are available, addons are added to an app using Object.values(NervaProductAddons)
  nerva: [],
  meno: [],
  quit: [],
  pain: [],
});

export const AVAILABLE_ONE_CLICK_UPSELL_PLANS = appOverride<SubscriptionPlan[]>({
  default: [],
  nerva: Object.values(MoodPlan),
});

export const AVAILABLE_TRIAL_PLANS: TrialPlan[] = [
  TrialPlan.FreeTrial,
  TrialPlan.OneDollarTrial,
  TrialPlan.SixteenDollarTrial,
];

export const DEFAULT_PURCHASE_ITEM_MAP: VariantMap<PurchaseItem> = {
  nerva: {
    trialPeriodDays: TrialPeriodDays.Seven,
    plan: NervaPlan.NervaFullSubscriptionAnnual,
    trialPlan: TrialPlan.FreeTrial,
  },
  meno: {
    trialPeriodDays: TrialPeriodDays.Seven,
    plan: MenoPlan.MenoFullSubscription,
    trialPlan: TrialPlan.FreeTrial,
  },
  quit: {
    trialPeriodDays: TrialPeriodDays.Seven,
    plan: QuitPlan.QuitFullSubscription3Month,
    trialPlan: TrialPlan.FreeTrial,
  },
  pain: {
    trialPeriodDays: TrialPeriodDays.Seven,
    plan: PainPlan.SubFullAnnual_199,
    trialPlan: TrialPlan.FreeTrial,
  },
};
export const DEFAULT_PURCHASE_ITEM = appVariant(DEFAULT_PURCHASE_ITEM_MAP);

export enum PlanQueryParam {
  /** 7 day trial + 12 month subscription */
  SevenDayTwelveMonth = '7d12mth',
  /** 14 day trial + 12 month subscription */
  FourteenDayTwelveMonth = '14d12mth',
  /** 7 day trial + 3 month subscription */
  SevenDayThreeMonth = '7d3mth',
}

/** A `plan` and `trialPeriodDays` combination. */
export type PurchaseItem = Pick<PurchaseState, 'trialPeriodDays' | 'plan' | 'trialPlan'>;

type PlanQueryParamToState = Partial<Record<PlanQueryParam, PurchaseItem>>;
/** A mapping of available `plan` query params to the `plan` and `trialPeriodDays` state for each app. */
export const PLAN_QUERY_PARAM_TO_STATE_MAP: Record<App, PlanQueryParamToState> = {
  nerva: {
    [PlanQueryParam.SevenDayThreeMonth]: {
      trialPeriodDays: TrialPeriodDays.Seven,
      plan: NervaPlan.NervaFullSubscription,
      trialPlan: TrialPlan.FreeTrial,
    },
    [PlanQueryParam.FourteenDayTwelveMonth]: {
      trialPeriodDays: TrialPeriodDays.Fourteen,
      plan: NervaPlan.NervaFullSubscriptionAnnual,
      trialPlan: TrialPlan.FreeTrial,
    },
    [PlanQueryParam.SevenDayTwelveMonth]: {
      trialPeriodDays: TrialPeriodDays.Seven,
      plan: NervaPlan.NervaFullSubscriptionAnnual,
      trialPlan: TrialPlan.FreeTrial,
    },
  },
  meno: {}, // `plan` query param not available for Meno
  mood: {}, // `plan` query param not available for Mood
  quit: {}, // `plan` query param not available for Quit
  pain: {}, // `plan` query param not available for Pain
};
/** A mapping of available `plan` query params to the `plan` and `trialPeriodDays` state. */
const PLAN_QUERY_PARAM_TO_STATE = appVariant(PLAN_QUERY_PARAM_TO_STATE_MAP);

/** The `plan` and `trialPeriodDays` from the `plan` query param. */
export function purchaseItemFromQueryParam(): PurchaseItem | undefined {
  const planQueryParam = new URLSearchParams(window.location.search).get('plan') ?? undefined;
  return PLAN_QUERY_PARAM_TO_STATE[planQueryParam as PlanQueryParam];
}

/** Checks if a coupon query params exists */
export function hasCouponFromQueryParam(): boolean {
  return isNonEmptyString(couponCodeFromQueryParam());
}

const PROGRAM_LENGTH_MAP: Record<App, { days: number; weeks: number }> = {
  nerva: { days: 42, weeks: 6 },
  meno: { days: 35, weeks: 5 },
  pain: { days: 42, weeks: 6 },
  quit: { days: 21, weeks: 3 },
  mood: { days: 36, weeks: 12 },
};

export const PROGRAM_LENGTH = PROGRAM_LENGTH_MAP[Environment.APP];

// The interval referenced here should only be used for displaying the weekly price. It's not exact.
const INTERVAL_DETAILS: Record<string, SubscriptionInterval> = {
  oneMonth: { inText: 'month', inMonths: 1, inWeeks: 4 },
  threeMonth: { inText: '3 months', inWeeks: 13, inMonths: 3 },
  annual: { inText: '12 months', inWeeks: 52, inMonths: 12 },
  lifetime: { inText: 'once', inWeeks: 0, inMonths: 0 },
};

export const PLAN_INTERVALS: Record<SubscriptionPlan, SubscriptionInterval> = {
  // Nerva
  [NervaPlan.NervaFullSubscription]: INTERVAL_DETAILS.threeMonth,
  [NervaPlan.NervaFullSubscriptionAnnual]: INTERVAL_DETAILS.annual,
  [NervaPlan.NervaFullSubscription3MonthAnnualPrice]: INTERVAL_DETAILS.threeMonth,
  [NervaPlan.NervaProgram]: INTERVAL_DETAILS.threeMonth,
  [NervaPlan.NervaOneOff_399]: INTERVAL_DETAILS.lifetime,
  [NervaPlan.NervaOneOff_399WithTrial]: INTERVAL_DETAILS.lifetime,
  // Meno
  [MenoPlan.MenoFullSubscription]: INTERVAL_DETAILS.threeMonth,
  [MenoPlan.MenoFullAnnual_199]: INTERVAL_DETAILS.annual,
  // Mood
  [MoodPlan.MoodAnnualSubscription_49]: INTERVAL_DETAILS.annual,
  [MoodPlan.MoodAnnualSubscription_99]: INTERVAL_DETAILS.annual,
  // Quit
  [QuitPlan.QuitFullLifetime]: INTERVAL_DETAILS.lifetime,
  [QuitPlan.QuitFullSubscription3Month]: INTERVAL_DETAILS.threeMonth,
  [QuitPlan.QuitFullSubscriptionMonthly]: INTERVAL_DETAILS.oneMonth,
  [QuitPlan.QuitFullSubscriptionAnnual]: INTERVAL_DETAILS.annual,
  [PainPlan.SubFullAnnual_199]: INTERVAL_DETAILS.annual,
};
